const DRAG_SCROLL_BLOCK_SELECTOR = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  "option",
  "label",
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[contenteditable="true"]',
  '[draggable="true"]',
  "[data-no-drag-scroll]",
].join(",");

export const HORIZONTAL_DRAG_SCROLL_DRAGGING_CLASS = "gs3-drag-scroll--dragging";
export const HORIZONTAL_DRAG_SCROLL_SCROLLABLE_CLASS = "gs3-drag-scroll--scrollable";

const DRAG_THRESHOLD_PX = 4;

type DragState = {
  container: HTMLElement;
  pointerId: number;
  startClientX: number;
  startScrollLeft: number;
  dragging: boolean;
};

let activeDrag: DragState | null = null;
let initialized = false;

/** Test-only reset — keeps module singleton isolated between specs. */
export function __resetHorizontalDragScrollStateForTests(): void {
  initialized = false;
  activeDrag = null;
}

export function isHorizontalDragScrollBlocked(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return true;
  }

  return Boolean(target.closest(DRAG_SCROLL_BLOCK_SELECTOR));
}

export function findHorizontalScrollContainer(
  start: EventTarget | null,
): HTMLElement | null {
  if (!(start instanceof Element)) {
    return null;
  }

  let element: HTMLElement | null =
    start instanceof HTMLElement ? start : start.parentElement;

  while (element) {
    const { overflowX } = getComputedStyle(element);

    if (
      (overflowX === "auto" || overflowX === "scroll") &&
      element.scrollWidth > element.clientWidth + 1
    ) {
      return element;
    }

    element = element.parentElement;
  }

  return null;
}

function suppressClickOnce(event: MouseEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

function endDrag(state: DragState): void {
  state.container.classList.remove(HORIZONTAL_DRAG_SCROLL_DRAGGING_CLASS);

  if (state.dragging) {
    state.container.addEventListener("click", suppressClickOnce, {
      capture: true,
      once: true,
    });
  }

  activeDrag = null;
}

export function initHorizontalDragScroll(): () => void {
  if (initialized || typeof window === "undefined") {
    return () => {};
  }

  initialized = true;

  const onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse" || event.button !== 0) {
      return;
    }

    if (isHorizontalDragScrollBlocked(event.target)) {
      return;
    }

    const container = findHorizontalScrollContainer(event.target);
    if (!container) {
      return;
    }

    container.classList.add(HORIZONTAL_DRAG_SCROLL_SCROLLABLE_CLASS);

    activeDrag = {
      container,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: container.scrollLeft,
      dragging: false,
    };
  };

  const onPointerMove = (event: PointerEvent): void => {
    const state = activeDrag;
    if (!state || event.pointerId !== state.pointerId) {
      return;
    }

    const deltaX = event.clientX - state.startClientX;

    if (!state.dragging) {
      if (Math.abs(deltaX) < DRAG_THRESHOLD_PX) {
        return;
      }

      state.dragging = true;
      state.container.classList.add(HORIZONTAL_DRAG_SCROLL_DRAGGING_CLASS);
    }

    event.preventDefault();
    state.container.scrollLeft = state.startScrollLeft - deltaX;
  };

  const onPointerUp = (event: PointerEvent): void => {
    const state = activeDrag;
    if (!state || event.pointerId !== state.pointerId) {
      return;
    }

    endDrag(state);
  };

  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerUp);

  return () => {
    document.removeEventListener("pointerdown", onPointerDown);
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerUp);
    initialized = false;
    activeDrag = null;
  };
}
